import React, { useEffect, useState, ReactNode, useRef } from 'react';
import * as Location from 'expo-location';
import { Map, useMap, GeolocateControl, Marker } from 'react-map-gl';
import * as geofire from 'geofire-common';

import { View, Center, Spinner } from 'native-base';
import { theme } from '@breakroom-nx/assets';
import { MapChat, MapRegionLoading } from '@breakroom-nx/ui';

import { Room, generateMarkers, getNewBounds } from '@breakroom-nx/classes';

// Types
import {
  Status,
  PermissionStatus,
  LatLng,
  Coordinate,
} from '@breakroom-nx/helpers';

// APIs
import { GeoPoint } from 'firebase/firestore';

import { auth, reverseGeo } from '@breakroom-nx/apis';

// Redux
import {
  useAppSelector,
  useAppDispatch,
  openModal,
  selectRoom,
  toggleChatRoomPanel,
  useGetRoomsForRegionQuery,
  useLazyGetRoomsForRegionQuery,
  useUpdateUserLocationMutation,
  setBounds,
} from '@breakroom-nx/redux';

const brooklyn = [40.62, -73.975];
const MAPBOX_CONFIG = {
  initialLongitude: brooklyn[1],
  initialLatitude: brooklyn[0],
  mapStyle: 'mapbox://styles/away-yc/cldw6k3s5004q01pcxx1h1ogs',
  accessToken:
    'pk.eyJ1IjoiYXdheS15YyIsImEiOiJja3RwOGdvN2gwazdjMnVwbWdiZzZmNmx1In0.tishTAuWkO34B2Np2z2alw',
  projection: 'globe',
};

interface GlobeProps {
  mobile?: boolean;
  autospin?: boolean;
  generateRandomData?: boolean;
  useGeolocation?: boolean;
}

function Globe({
  mobile = false,
  autospin = false,
  useGeolocation = true,
  generateRandomData = false,
}: GlobeProps) {
  // Redux
  const dispatch = useAppDispatch();
  const [updateUserLocation, updateLocationState] =
    useUpdateUserLocationMutation();

  // Generating Map Bounds
  const [mapLoading, setMapLoading] = useState<boolean>(true);
  const markers: ReactNode[] = [];
  const radiusInMeters = 50 * 1000;
  const [mapIdle, setMapIdle] = useState<boolean>(false);
  const geolocateControlRef = React.useRef(null);
  const [locationServiceStatus, setLocationServiceStatus] =
    useState<PermissionStatus>(PermissionStatus.NotSet);

  const boundsRef = useRef<string[][]>();

  const [trigger, { data, isLoading, isError }] =
    useLazyGetRoomsForRegionQuery();

  useEffect(() => {
    // console.log(`Rendered Map`);
  });

  // Gets User's Approximate Location Using Their IP Address
  const getLocationFromIP = () => {
    fetch('https://ipapi.co/json/')
      .then((response) => response.json())
      .then((data) => {
        const latitude = data.latitude as number;
        const longitude = data.longitude as number;
        const city = data.city as string;
        const country = data.country_name as string;
        const geoCode = data.country_code as string;
        const approximateLocation: Location.LocationObject = {
          coords: {
            latitude: latitude,
            longitude: longitude,
            altitude: null,
            altitudeAccuracy: null,
            accuracy: null,
            heading: null,
            speed: null,
          },
          timestamp: 0,
        };

        // Convert to Geohash and Update User Account on Firebase
        const bounds = geofire.geohashQueryBounds(
          [latitude, longitude],
          radiusInMeters
        );

        boundsRef.current = bounds;

        // console.log(
        //   `Approximate IP Coords: ${approximateLocation} Bounds: ${bounds}`
        // );

        const location = new GeoPoint(latitude, longitude);
        const geohash = geofire.geohashForLocation([latitude, longitude]);

        updateUserLocation({
          location: location,
          geohash: geohash,
          city: city,
          country: country,
          geoCode: geoCode.toUpperCase(),
        });
      })
      .catch((error) => {
        // console.log(`Location Retrieval from IP Failed`);
        dispatch(
          openModal({
            modalType: 'Alert',
            modalProps: {
              status: Status.warning,
              title: error,
            },
          })
        );
      });
  };

  // Attempt to Load GeoLocation API
  function checkGeolocation() {
    // console.log(`[MAP] Getting user's location`);
    async function success(position: GeolocationPosition) {
      // console.log(`GeoLocation API Available`);
      setLocationServiceStatus(PermissionStatus.Enabled);

      const exactLocation: Location.LocationObject = {
        coords: {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          altitude: position.coords.altitude,
          altitudeAccuracy: position.coords.altitudeAccuracy,
          accuracy: position.coords.accuracy,
          heading: position.coords.heading,
          speed: position.coords.speed,
        },
        timestamp: position.timestamp,
      };

      // Convert to Geohash and Update User Account on Firebase
      const bounds = geofire.geohashQueryBounds(
        [exactLocation.coords.latitude, exactLocation.coords.longitude],
        radiusInMeters
      );

      boundsRef.current = bounds;

      // console.log(
      //   `GeoLocation: [${exactLocation.coords.latitude}, ${exactLocation.coords.longitude}] Bounds: ${bounds}`
      // );

      const location = new GeoPoint(
        exactLocation.coords.latitude,
        exactLocation.coords.longitude
      );
      const geohash = geofire.geohashForLocation([
        exactLocation.coords.latitude,
        exactLocation.coords.longitude,
      ]);
      const geoData = await reverseGeo(location);
      // console.log(`Update Profile`);

      await updateUserLocation({
        location: location,
        geohash: geohash,
        city: geoData?.city,
        country: geoData?.country,
        geoCode: geoData?.code.toUpperCase(),
      });
    }

    function error() {
      // console.log(`GeoLocation API Unavailable`);
      setLocationServiceStatus(PermissionStatus.Unavailable);
      // getLocationFromIP();
    }

    if (!navigator.geolocation) {
      error();
    } else {
      navigator.geolocation.getCurrentPosition(success, error);
    }
  }

  function AutoSpin() {
    const { current: map } = useMap();

    const onClick = () => {
      const secondsPerRevolution = 120;
      const maxSpinZoom = 5;
      const slowSpinZoom = 3;
      const userInteracting = false;
      const spinEnabled = true;

      if (map) {
        const zoom = map.getZoom();

        if (spinEnabled && !userInteracting && zoom < maxSpinZoom) {
          let distancePerSecond = 360 / secondsPerRevolution;
          if (zoom > slowSpinZoom) {
            // Slow spinning at higher zooms
            const zoomDif = (maxSpinZoom - zoom) / (maxSpinZoom - slowSpinZoom);
            distancePerSecond *= zoomDif;
          }
          const center = map.getCenter();
          center.lng -= distancePerSecond;
          // Smoothly animate the map over one second.
          // When this animation is complete, it calls a 'moveend' event.
          map.easeTo({ center, duration: 1000, easing: (n) => n });
        }
      }
    };

    useEffect(() => {
      if (autospin) {
        const intervalId = setInterval(() => {
          onClick();
        }, 1000);

        return () => clearInterval(intervalId);
      }
    });

    return null;
  }

  function _placeMarkers(markers?: Room[]) {
    if (markers && markers.length > 0) {
      return markers.map((room) => {
        // console.log(`Placing room into marker: ${room.title}`);
        return (
          <Marker
            latitude={room.location.latitude}
            longitude={room.location.longitude}
            anchor="bottom"
            key={room.id}
          >
            <MapChat room={room} />
          </Marker>
        );
      });
    } else {
      return null;
    }
  }

  async function getChatsForRegion() {
    // console.log(`Getting Chats for Region!`);
    // console.log(`Current Bounds: ${boundsRef.current}`);
    if (boundsRef.current) {
      trigger(boundsRef.current).then((value) => {
        if (value.data && value.data.length < 1) {
          dispatch(openModal({ modalType: 'NoRoomsFound' }));
        }
      });
    }
  }

  const onMapLoad = () => {
    // console.log('[MAP] Map loaded');
    setMapLoading(false);
    if (useGeolocation) {
      checkGeolocation();
    }
  };

  const onMapIdle = (e: mapboxgl.MapboxEvent<undefined>) => {
    // console.log(`[MAP] Map is idle`);
    setMapIdle(false);

    // Convert to Geohash and Update User Account on Firebase
    const mapBounds = e.target.getBounds();
    const mapCenter = e.target.getCenter();
    const newBounds = getNewBounds(mapBounds);

    // console.log(`New Bounds: ${newBounds}`);
    boundsRef.current = newBounds;

    // Convert center to numbers and set for redux
    const lat = mapCenter.lat;
    const lng = mapCenter.lng;
    const center: Coordinate = { latitude: lat, longitude: lng };
    dispatch(setBounds(center));
  };

  return (
    <>
      {mapLoading && (
        <Center
          height={'full'}
          width={'full'}
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            zIndex: 9999,
            position: 'absolute',
          }}
        >
          <Spinner color={theme.colors.primary[400]} size="lg" />
        </Center>
      )}
      <Map
        id="globe"
        reuseMaps
        initialViewState={{
          longitude: MAPBOX_CONFIG.initialLongitude,
          latitude: MAPBOX_CONFIG.initialLatitude,
          zoom: 2.6,
        }}
        mapStyle={MAPBOX_CONFIG.mapStyle}
        mapboxAccessToken={MAPBOX_CONFIG.accessToken}
        projection={MAPBOX_CONFIG.projection}
        onMove={() => {
          if (!generateRandomData) {
            setMapIdle(true);
          }
        }}
        onLoad={onMapLoad}
        onIdle={(e) => onMapIdle(e)}
        logoPosition="bottom-right"
        style={{
          height: '100vh',
          width: '100vw',
          position: 'fixed',
        }}
      >
        <GeolocateControl
          ref={geolocateControlRef}
          showAccuracyCircle={false}
          showUserLocation={true}
          trackUserLocation={true}
          style={{
            zIndex: -9999,
            position: 'fixed',
          }}
        />
        {_placeMarkers(generateRandomData ? generateMarkers(20) : data)}
        <AutoSpin />
      </Map>
      {useGeolocation && (
        <View
          position={'fixed'}
          left={'50%'}
          width={'fit-content'}
          bottom={{ base: '65px', lg: '90px' }}
          style={{
            transform: 'translate(-50%, -50%)',
          }}
        >
          <MapRegionLoading
            isDisabled={mapIdle}
            isLoading={isLoading}
            onGetRooms={getChatsForRegion}
            locationServiceStatus={locationServiceStatus}
            geolocationControlRef={geolocateControlRef}
          />
        </View>
      )}
    </>
  );
}

export default React.memo(Globe);
